import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";

const WEBSOCKET_URL =
  process.env.REACT_APP_WS_URL ||
  "wss://stkr3op8pe.execute-api.ap-south-1.amazonaws.com/production/";

const GameContext = createContext(null);

// Parses every server message into shared state + fires typed events for effects.
// Event types: 'error' | 'message' | 'roomStatus' | 'answer' | 'gameStarted'
//              | 'roomClosed' | 'result' | 'questionsLoaded' | 'reconnected'
export const GameProvider = ({ children }) => {
  const ws = useRef(null);
  const listeners = useRef(new Map());
  const outbox = useRef([]);
  const lastRoomId = useRef(null);
  const reconnectAttempts = useRef(0);
  const disposed = useRef(false);

  const [connected, setConnected] = useState(false);
  const [room, setRoom] = useState(null);
  const [userId, setUserId] = useState("");
  const [team, setTeam] = useState("");
  const [questionsLoaded, setQuestionsLoaded] = useState(false);
  const [result, setResult] = useState(null);

  const emit = useCallback((type, payload) => {
    const subs = listeners.current.get(type);
    if (subs) subs.forEach((cb) => cb(payload));
  }, []);

  const subscribe = useCallback((type, cb) => {
    if (!listeners.current.has(type)) listeners.current.set(type, new Set());
    listeners.current.get(type).add(cb);
    return () => listeners.current.get(type)?.delete(cb);
  }, []);

  const handleData = useCallback(
    (data) => {
      if (data.error) {
        emit("error", data.error);
        return;
      }
      if (data.current_user_id) {
        setUserId(data.current_user_id);
      }
      if (data.player_team) {
        setTeam(data.player_team);
      }
      if (data.roomStatus) {
        setRoom(data.roomStatus);
        emit("roomStatus", data.roomStatus);
        if (data.isResult) {
          setResult(data.gameResult || null);
          emit("result", data.gameResult);
        }
      }
      if (data.submitted_answer !== undefined) {
        emit("answer", data);
      }
      if (data.message) {
        if (data.message === "Questions Loaded") {
          setQuestionsLoaded(true);
          emit("questionsLoaded");
        } else if (data.message === "Game started") {
          emit("gameStarted");
        } else if (data.message === "Room Closed") {
          emit("roomClosed");
        }
        if (data.message !== "hidden" && data.message !== "undefined") {
          emit("message", data.message);
        }
      }
    },
    [emit]
  );

  const handleDataRef = useRef(handleData);
  handleDataRef.current = handleData;

  useEffect(() => {
    disposed.current = false;

    const connect = () => {
      if (disposed.current) return;
      const socket = new WebSocket(WEBSOCKET_URL);
      ws.current = socket;

      const wasReconnect = reconnectAttempts.current > 0;
      socket.onopen = () => {
        if (ws.current !== socket) return;
        reconnectAttempts.current = 0;
        setConnected(true);
        // Flush anything queued while the socket was down.
        while (outbox.current.length > 0) {
          socket.send(JSON.stringify(outbox.current.shift()));
        }
        if (lastRoomId.current && wasReconnect) {
          emit("reconnected");
        }
      };
      socket.onmessage = (event) => {
        if (ws.current !== socket) return;
        try {
          handleDataRef.current(JSON.parse(event.data));
        } catch (e) {
          console.error("Bad message from server", e);
        }
      };
      socket.onerror = (err) => {
        if (ws.current === socket) console.error("WebSocket error:", err);
      };
      socket.onclose = () => {
        // Ignore sockets we've already replaced (e.g. StrictMode remount).
        if (disposed.current || ws.current !== socket) return;
        setConnected(false);
        const delay = Math.min(1000 * 2 ** reconnectAttempts.current, 10000);
        reconnectAttempts.current += 1;
        setTimeout(connect, delay);
      };
    };

    connect();
    return () => {
      disposed.current = true;
      ws.current?.close();
    };
  }, [emit]);

  const sendMessage = useCallback((msg) => {
    if (msg.roomId) lastRoomId.current = msg.roomId;
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(msg));
    } else {
      outbox.current.push(msg);
    }
  }, []);

  const resetGame = useCallback(() => {
    setRoom(null);
    setResult(null);
    setQuestionsLoaded(false);
    setTeam("");
    setUserId("");
    lastRoomId.current = null;
  }, []);

  return (
    <GameContext.Provider
      value={{
        connected,
        room,
        userId,
        team,
        questionsLoaded,
        result,
        sendMessage,
        subscribe,
        resetGame,
        setQuestionsLoaded,
      }}
    >
      {children}
    </GameContext.Provider>
  );
};

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) throw new Error("useGame must be used within a GameProvider");
  return context;
};

// Subscribe to a game event for the lifetime of the component.
export const useGameEvent = (type, handler) => {
  const { subscribe } = useGame();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => subscribe(type, (payload) => ref.current(payload)), [subscribe, type]);
};
